import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { convert as htmlToText } from 'html-to-text'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { NavigateFunction } from 'react-router'

import { Field, Toggle } from '@mangovoice/mango-ui'
import type { ExtensionWebhookConfig } from '@mangovoice/types'

import type { WebhooksApi } from '../api/webhooksApi'
import { useGetIntegration } from '../hooks/useGetIntegration'
import { usePatchExtensionIntegration } from '../hooks/usePatchExtensionIntegration'
import { usePatchIntegration } from '../hooks/usePatchIntegration'
import { formatName, REMOTE_ID_REGEX } from '../utils'
import { DolphinConfigForm } from './DolphinConfigForm'
import { WebhooksV2ConfigForm } from './WebhooksV2ConfigForm'
import { XLDentExtensionTable } from './XLDentExtensionTable'

const INTEGRATION_IDS = {
  YAPI: 2,
  LEGWORK: 4,
  XLDENT: 5,
  FLEX_DENTAL: 8,
  WEBHOOKS_V2: 6,
  DOLPHIN: 28,
} as const

// YAPI/Legwork/Flex Dental use a different auth2 format — skip the standard Remote ID validation.
const INTEGRATION_IDS_WITH_AUTH2_HELPER = new Set<number>([
  INTEGRATION_IDS.YAPI,
  INTEGRATION_IDS.LEGWORK,
  INTEGRATION_IDS.FLEX_DENTAL,
])

function getAuth2Label(
  integrationId: number,
  t: (key: string) => string,
): string {
  if (integrationId === INTEGRATION_IDS.YAPI)
    return t('webhooks.detail.practiceGuidLabel')
  return t('webhooks.detail.remoteIdLabel')
}

interface StatusSwitchProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
}

const StatusSwitch = ({
  checked,
  onCheckedChange,
  disabled,
}: StatusSwitchProps) => {
  const { t } = useTranslation()
  return (
    <Box display="flex" alignItems="center" gap={3} flexShrink={0}>
      <Text fontSize="sm" color="fg.muted">
        {t('webhooks.detail.status')}
      </Text>
      <Badge
        colorPalette={checked ? 'success' : 'neutral'}
        variant="subtle"
        size="md"
      >
        {checked ? t('webhooks.detail.on') : t('webhooks.detail.off')}
      </Badge>
      <Toggle
        checked={checked}
        onCheckedChange={({ checked: c }) => onCheckedChange(c)}
        disabled={disabled}
        colorPalette="brand"
      />
    </Box>
  )
}

interface RemoteIdFormProps {
  label: string
  value: string
  onChange: (value: string) => void
  fieldError: string | null
  isPending: boolean
  isError: boolean
  patchErrorMessage: string | undefined
  documentationUrl: string | null | undefined
  isConfigured: boolean
  required?: boolean
  onSave: () => void
}

const RemoteIdForm = ({
  label,
  value,
  onChange,
  fieldError,
  isPending,
  isError,
  patchErrorMessage,
  documentationUrl,
  isConfigured,
  required,
  onSave,
}: RemoteIdFormProps) => {
  const { t } = useTranslation()
  return (
    <>
      <Box maxW="560px" width="full" mx="auto">
        <Field.Root required={required} invalid={!!fieldError || isError}>
          <Field.Label>{label}</Field.Label>
          <Input value={value} onChange={(e) => onChange(e.target.value)} />
          {fieldError && <Field.ErrorText>{fieldError}</Field.ErrorText>}
          {!fieldError && isError && (
            <Field.ErrorText>
              {patchErrorMessage ?? t('webhooks.common.failedToSave')}
            </Field.ErrorText>
          )}
          {documentationUrl && (
            <Box textAlign="right" mt={1}>
              <a
                href={documentationUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 'var(--chakra-font-sizes-sm)',
                  color: 'var(--chakra-colors-fg-muted)',
                  textDecoration: 'underline',
                }}
              >
                {t('webhooks.common.learnMore')}
              </a>
            </Box>
          )}
        </Field.Root>
      </Box>
      <Box display="flex" justifyContent="center">
        <Button
          onClick={onSave}
          loading={isPending}
          css={{
            backgroundColor: 'var(--chakra-colors-brand-solid)',
            color: 'var(--chakra-colors-brand-contrast)',
            _hover: {
              backgroundColor: 'var(--chakra-colors-brand-emphasized)',
            },
          }}
        >
          {isConfigured
            ? t('webhooks.common.updateIntegration')
            : t('webhooks.common.createIntegration')}
        </Button>
      </Box>
    </>
  )
}

interface IntegrationLogoProps {
  imgUrl: string | null | undefined
  name: string
  imageBaseUrl: string
  isBeta: boolean
}

const IntegrationLogo = ({
  imgUrl,
  name,
  imageBaseUrl,
  isBeta,
}: IntegrationLogoProps) => {
  const { t } = useTranslation()
  return (
    <Box display="flex" alignItems="center" gap={2}>
      {imgUrl && (
        <img
          src={`${imageBaseUrl}${imgUrl}`}
          alt={name}
          style={{ maxHeight: 40, width: 'auto' }}
        />
      )}
      {isBeta && (
        <Badge colorPalette="warning" variant="subtle">
          {t('webhooks.common.beta')}
        </Badge>
      )}
    </Box>
  )
}

export interface WebhookDetailPageProps {
  api: WebhooksApi
  pbxId: number
  integrationId: number
  navigate: NavigateFunction
  imageBaseUrl?: string
  listRoute?: string
  onToggleError?: () => void
}

export const WebhookDetailPage = ({
  api,
  pbxId,
  integrationId,
  navigate,
  imageBaseUrl = '',
  listRoute,
  onToggleError,
}: WebhookDetailPageProps) => {
  const { t } = useTranslation()
  const {
    data: integration,
    isLoading,
    error,
  } = useGetIntegration({ api, pbxId, integrationId })

  const patchIntegration = usePatchIntegration({ api, pbxId, integrationId })
  const patchIntegrationStatus = usePatchIntegration({
    api,
    pbxId,
    integrationId,
    onError: onToggleError,
  })
  const patchExtension = usePatchExtensionIntegration({
    api,
    pbxId,
    integrationId,
  })

  const [auth2Value, setAuth2Value] = useState<string | null>(null)
  const [auth2Error, setAuth2Error] = useState<string | null>(null)

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <Spinner />
      </Box>
    )
  }

  if (error || !integration) {
    return (
      <Box p={6}>
        <Text color="fg.error">{t('webhooks.detail.failedToLoad')}</Text>
      </Box>
    )
  }

  const isActive = integration.activation.is_active
  const isXLDent = integrationId === INTEGRATION_IDS.XLDENT
  const isWebhooksV2 = integrationId === INTEGRATION_IDS.WEBHOOKS_V2
  const isDolphin = integrationId === INTEGRATION_IDS.DOLPHIN

  const description = isWebhooksV2
    ? t('webhooks.detail.webhooksV2Description')
    : isXLDent
      ? t('webhooks.detail.xlDentDescription')
      : htmlToText(integration.description || integration.offer || '', {
          wordwrap: false,
        })

  const currentAuth2 = auth2Value ?? integration.auth2 ?? ''
  const isConfigured =
    integration.is_configured ?? integration.pbx_integration_id !== null

  const handleToggleActive = (checked: boolean) => {
    patchIntegrationStatus.mutate({ pbx_id: pbxId, active: checked })
  }

  const handleSaveAuth2 = () => {
    if (!INTEGRATION_IDS_WITH_AUTH2_HELPER.has(integrationId)) {
      if (!REMOTE_ID_REGEX.test(currentAuth2)) {
        setAuth2Error(
          'Two characters minimum. Must begin and end with letter(s) or number(s). May use dash character as separator. e.g. Mango-43aC-Eaq7',
        )
        return
      }
    }
    setAuth2Error(null)
    patchIntegration.mutate({ pbx_id: pbxId, auth2: currentAuth2 })
  }

  const handleExtensionToggle = (
    ext: ExtensionWebhookConfig,
    checked: boolean,
  ) => {
    patchExtension.mutate({
      extId: ext.ext_id,
      data: { pbx_id: pbxId, active: checked },
    })
  }

  const handleSaveRemoteUser = (
    ext: ExtensionWebhookConfig,
    remoteUser: string,
  ) => {
    patchExtension.mutate({
      extId: ext.ext_id,
      data: { pbx_id: pbxId, remote_user: remoteUser },
    })
  }

  return (
    <Box overflowY="auto" height="full" width="full">
      <Box p={6}>
        <Button
          variant="ghost"
          size="sm"
          mb={4}
          onClick={() => (listRoute ? navigate(listRoute) : navigate(-1))}
        >
          {t('webhooks.detail.back')}
        </Button>

        <Card.Root>
          <Card.Body display="flex" flexDirection="column" gap={5}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <IntegrationLogo
                imgUrl={integration.img_url}
                name={integration.name}
                imageBaseUrl={imageBaseUrl}
                isBeta={integration.status === 'pending'}
              />
              <StatusSwitch
                checked={isActive}
                onCheckedChange={handleToggleActive}
                disabled={patchIntegrationStatus.isPending}
              />
            </Box>

            <Heading size="xl" textAlign="center">
              {isWebhooksV2
                ? t('webhooks.detail.webhooksV2Title')
                : isXLDent
                  ? t('webhooks.detail.xlDentTitle')
                  : t('webhooks.detail.integrationTitle', {
                      name: formatName(integration.name),
                    })}
            </Heading>

            {description && (
              <Text textAlign="center" color="fg.muted" whiteSpace="pre-line">
                {description}
              </Text>
            )}

            {isDolphin ? (
              <DolphinConfigForm
                integration={integration}
                isPending={patchIntegration.isPending}
                isError={patchIntegration.isError}
                error={patchIntegration.error}
                onSave={(data) =>
                  patchIntegration.mutate({ pbx_id: pbxId, ...data })
                }
              />
            ) : isWebhooksV2 ? (
              <WebhooksV2ConfigForm
                integration={integration}
                isPending={patchIntegration.isPending}
                isError={patchIntegration.isError}
                error={patchIntegration.error}
                onSave={(data) =>
                  patchIntegration.mutate({ pbx_id: pbxId, ...data })
                }
              />
            ) : isXLDent ? (
              <XLDentExtensionTable
                extensions={integration.extensions}
                isActive={isActive}
                isPatchPending={patchExtension.isPending}
                onToggle={handleExtensionToggle}
                onSave={handleSaveRemoteUser}
              />
            ) : (
              <RemoteIdForm
                label={getAuth2Label(integrationId, t)}
                value={currentAuth2}
                onChange={(v) => {
                  setAuth2Value(v)
                  setAuth2Error(null)
                }}
                fieldError={auth2Error}
                isPending={patchIntegration.isPending}
                isError={patchIntegration.isError}
                patchErrorMessage={patchIntegration.error?.message}
                documentationUrl={integration.documentation_url}
                isConfigured={isConfigured}
                required={!INTEGRATION_IDS_WITH_AUTH2_HELPER.has(integrationId)}
                onSave={handleSaveAuth2}
              />
            )}
          </Card.Body>
        </Card.Root>
      </Box>
    </Box>
  )
}
